import { useState, useEffect } from 'react';
import { Plus, FileText } from 'lucide-react';
import { Project, Matter, Goal, Task, Client, db, ProjectTemplate } from '../../lib/db';
import { ProjectCard } from './ProjectCard';
import { ProjectCardMobile } from './ProjectCardMobile';
import { ProjectForm } from './ProjectForm';
import { CreateFromProjectTemplateModal } from '../templates/CreateFromProjectTemplateModal';
import { ProjectTemplateSelectModal } from '../templates/ProjectTemplateSelectModal';

interface ProjectListProps {
  projects: Project[];
  matters: Matter[];
  goals: Goal[];
  tasks?: Task[];
  clients?: Client[];
  userId: string;
  onUpdate: () => void;
  onMarkComplete?: (project: Project) => void;
  onMarkIncomplete?: (project: Project) => void;
}

export function ProjectList({ projects, matters, goals, tasks, clients, userId, onUpdate, onMarkComplete, onMarkIncomplete }: ProjectListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [draggedProject, setDraggedProject] = useState<Project | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [projectTemplates, setProjectTemplates] = useState<ProjectTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);

  useEffect(() => {
    loadProjectTemplates();
  }, [userId]);

  const loadProjectTemplates = async () => {
    try {
      const templates = await db.projectTemplates.findByUserId(userId);
      setProjectTemplates(templates);
    } catch (error) {
      console.error('Error loading project templates:', error);
    }
  };

  const handleTemplateSelect = async () => {
    setShowTemplateModal(true);
  };

  const handleCreate = async (data: Partial<Project>) => {
    try {
      const maxPosition = Math.max(...projects.map(p => p.position), -1);
      await db.projects.create({
        ...data,
        user_id: userId,
        position: maxPosition + 1,
      } as Omit<Project, 'id' | 'created_at' | 'updated_at'>);
      setIsCreating(false);
      onUpdate();
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Failed to create project');
    }
  };

  const handleUpdate = async (data: Partial<Project>) => {
    if (!editingProject) return;

    try {
      await db.projects.update(editingProject.id, userId, data);
      setEditingProject(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    }
  };

  const handleDelete = async (project: Project) => {
    try {
      await db.projects.delete(project.id, userId);
      onUpdate();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project');
    }
  };

  const handleDragStart = (e: React.DragEvent, project: Project) => {
    setDraggedProject(project);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetProject: Project) => {
    e.preventDefault();
    
    if (!draggedProject || draggedProject.id === targetProject.id) {
      return;
    }

    const sortedProjects = [...projects].sort((a, b) => a.position - b.position);
    const draggedIndex = sortedProjects.findIndex(p => p.id === draggedProject.id);
    const targetIndex = sortedProjects.findIndex(p => p.id === targetProject.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const reorderedProjects = [...sortedProjects];
    const [removed] = reorderedProjects.splice(draggedIndex, 1);
    reorderedProjects.splice(targetIndex, 0, removed);

    try {
      await Promise.all(
        reorderedProjects.map((project, index) =>
          db.projects.update(project.id, userId, { position: index })
        )
      );
      onUpdate();
    } catch (error) {
      console.error('Error reordering projects:', error);
      alert('Failed to reorder projects');
    }

    setDraggedProject(null);
  };

  const sortedProjects = [...projects].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Projects</h2>
        <div className="flex gap-2">
          {projectTemplates.length > 0 && (
            <button
              onClick={handleTemplateSelect}
              className="inline-flex items-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-600 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <FileText className="h-4 w-4 mr-2" />
              From Template
            </button>
          )}
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </button>
        </div>
      </div>

      {isCreating && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Project</h3>
          <ProjectForm
            clients={clients}
            onSubmit={handleCreate}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      )}

      {editingProject && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Project</h3>
          <ProjectForm
            project={editingProject}
            clients={clients}
            onSubmit={handleUpdate}
            onCancel={() => setEditingProject(null)}
          />
        </div>
      )}

      {sortedProjects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-500">No projects yet. Create your first project to organize your matters and deliverables.</p>
        </div>
      ) : (
        <>
          {/* Desktop view */}
          <div className="hidden sm:block space-y-4">
            {sortedProjects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                matters={matters}
                goals={goals}
                tasks={tasks}
                userId={userId}
                onEdit={setEditingProject}
                onDelete={handleDelete}
                onMarkComplete={onMarkComplete}
                onMarkIncomplete={onMarkIncomplete}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onGoalsUpdate={onUpdate}
              />
            ))}
          </div>
          {/* Mobile view */}
          <div className="sm:hidden space-y-3">
            {sortedProjects.map(project => (
              <ProjectCardMobile
                key={project.id}
                project={project}
                matters={matters}
                goals={goals}
                tasks={tasks}
                onEdit={setEditingProject}
                onDelete={handleDelete}
                onMarkComplete={onMarkComplete}
                onMarkIncomplete={onMarkIncomplete}
              />
            ))}
          </div>
        </>
      )}
      
      {/* Template Selection Modal */}
      <ProjectTemplateSelectModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={projectTemplates}
        onSelect={(template) => {
          setSelectedTemplate(template);
          setShowTemplateModal(false);
        }}
      />
      
      {/* Create from Template Modal */}
      <CreateFromProjectTemplateModal
        isOpen={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        template={selectedTemplate}
        userId={userId}
        onSuccess={() => {
          setSelectedTemplate(null);
          onUpdate();
        }}
      />
    </div>
  );
}